import { createFuzzySearchIndex } from "app/pages/sales-intelligence/services/fuzzy-search/factory";
import { normalizeForSearch } from "app/pages/sales-intelligence/services/fuzzy-search/normalize";
import type { FuzzyFuseOptions } from "app/pages/sales-intelligence/services/fuzzy-search/types";

import type { SearchableSelectionOption } from "../../types";

import type { ColumnSearchDoc, IColumnSearchIndex } from "./types";

const SHORT_QUERY_LENGTH = 3;

/**
 * Short queries match almost anything, so they are held to a much tighter score.
 */
const SHORT_QUERY_THRESHOLD = 0.2;

/**
 * Measured against the real column labels rather than guessed, and it matches what the industry and
 * filter search indexes already use. Tighter values look appealing but silently drop transpositions:
 * bitap charges "revneue" as two edits, and at 0.3 it finds nothing at all once the match sits at an
 * offset, as it does in "Annual Revenue". Loosening to 0.35 added no extra results for any ordinary
 * query on this corpus, so the tighter value bought nothing.
 */
const LONG_QUERY_THRESHOLD = 0.35;

/**
 * fuse penalises matches by their distance from `location`, so a term buried deep in a long string
 * scores past any sane threshold and is dropped. Additional labels come straight from the DTO and
 * are unbounded, so they are clipped to a length matches can still be found in.
 */
const MAX_ADDITIONAL_LABEL_LENGTH = 60;

/**
 * Above the largest column set we ship (~6k). fuse applies `limit` after scoring, and the picker
 * filters the result by category *afterwards* — so a limit that actually bites would silently empty
 * a narrow category whenever the global top-N happened to contain none of its columns.
 */
const MAX_RESULTS = 10000;

const FUSE_OPTIONS: FuzzyFuseOptions<ColumnSearchDoc> = {
    shouldSort: true,
    location: 0,
    distance: 200,
    minMatchCharLength: 2,
    threshold: LONG_QUERY_THRESHOLD,
    includeScore: true,
    /*
     * One key, not two. fuse raises each matched key's score to the power of its weight, and a
     * fractional exponent pushes a sub-1 score *toward* 1 — so a low weight poisons a field rather
     * than de-prioritising it. Two keys at weight 1 is rejected outright ("Total of weights cannot
     * exceed 1"), so label and additional label are searched as one string. Keeping the label first
     * also preserves the old ranking, which measured position in exactly that concatenation.
     */
    keys: [{ name: "text", weight: 1 }],
};

const buildSearchText = (option: SearchableSelectionOption): string =>
    option.additionalLabel
        ? `${option.label} ${option.additionalLabel.slice(0, MAX_ADDITIONAL_LABEL_LENGTH)}`
        : option.label;

export const buildColumnSearchDocs = (
    source: readonly SearchableSelectionOption[],
    locale: string,
): readonly ColumnSearchDoc[] =>
    source.map((option) => ({ id: option.id, text: normalizeForSearch(buildSearchText(option), locale) }));

const scoreThresholdFor = (queryLength: number): number =>
    queryLength <= SHORT_QUERY_LENGTH ? SHORT_QUERY_THRESHOLD : LONG_QUERY_THRESHOLD;

/**
 * Fuzzy match over the column options. The underlying engine caches its normalised documents on the
 * source reference, so callers must hand it the full, stable option list rather than an already
 * filtered slice — otherwise every keystroke re-normalises the whole set.
 */
export const createColumnSearchIndex = (): IColumnSearchIndex => {
    const engine = createFuzzySearchIndex<readonly SearchableSelectionOption[], string, ColumnSearchDoc>({
        buildDocs: buildColumnSearchDocs,
        fuseOptions: FUSE_OPTIONS,
        scoreThresholdFor,
        limit: MAX_RESULTS,
    });

    return {
        match: (params) => new Map(engine.search(params).map((hit) => [hit.id, hit.score])),
        invalidate: engine.invalidate,
    };
};
